import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type EngagementStatus = 'green' | 'yellow' | 'red';

const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;
const TEN_DAYS_MS = 10 * 24 * 60 * 60 * 1000;

export function classifyStatus(lastTrainedAt: string | null): EngagementStatus {
  if (!lastTrainedAt) return 'red';
  const elapsed = Date.now() - new Date(lastTrainedAt).getTime();
  if (elapsed <= FIVE_DAYS_MS) return 'green';
  if (elapsed <= TEN_DAYS_MS) return 'yellow';
  return 'red';
}

export function useStudentEngagement(studentIds: string[]): {
  statuses: Record<string, EngagementStatus>;
  isLoading: boolean;
  error: Error | null;
} {
  const { data = {}, isLoading, error } = useQuery({
    queryKey: ['student-engagement', [...studentIds].sort().join(',')],
    queryFn: async () => {
      const since = new Date(Date.now() - TEN_DAYS_MS).toISOString();
      const { data, error } = await supabase
        .from('workout_sessions')
        .select('student_id, completed_at')
        .in('student_id', studentIds)
        .not('completed_at', 'is', null)
        .gte('completed_at', since);
      if (error) throw error;

      const rows = data as { student_id: string; completed_at: string }[];
      const lastSeen: Record<string, string> = {};
      for (const row of rows) {
        if (!lastSeen[row.student_id] || new Date(row.completed_at) > new Date(lastSeen[row.student_id])) {
          lastSeen[row.student_id] = row.completed_at;
        }
      }

      const result: Record<string, EngagementStatus> = {};
      for (const id of studentIds) {
        result[id] = classifyStatus(lastSeen[id] ?? null);
      }
      return result;
    },
    enabled: studentIds.length > 0,
  });
  return { statuses: data, isLoading, error: error as Error | null };
}
